export type GamePhase = "home" | "running" | "gameover";
export type RunOutcome = "crash" | "complete";

export interface MissionView {
  type: string;
  icon: string;
  label: string;
  progress: number;
  target: number;
  done: boolean;
}

export interface MetaView {
  best: number;
  tokens: number;
  set: number;
  multiplier: number;
  completed: number;
  missions: MissionView[];
  activeMission: MissionView;
}

export interface HudView {
  speed: number;
  score: number;
  runTokens: number;
  multiplier: number;
  combo: number;
  flowProgress: number;
  nearMisses: number;
  modeLabel: string;
  timeRemaining: number | null;
  finalCountdown: number | null;
  stage: number;
  levelProgress: number;
  boost: number;
  nitroEnergy: number;
  nitroMaxEnergy: number;
  unlimitedNitro: boolean;
  magnet: number;
  doubleScore: number;
  reverse: number;
  speedLimit: number;
  steeringLimit: number;
  chaos: number;
  boostBlend: number;
}

export interface ResultView {
  score: number;
  distance: number;
  duration: number;
  stage: number;
  runTokens: number;
  nearMisses: number;
  bestCombo: number;
  modeLabel: string;
  outcome: RunOutcome;
}

export interface JoystickView {
  visible: boolean;
  x: number;
  y: number;
  dx: number;
  dy: number;
}

export interface MissionToast {
  id: number;
  text: string;
  kind: "progress" | "complete" | "set";
  icon: string;
}

export interface GameUiState {
  phase: GamePhase;
  hud: HudView;
  meta: MetaView;
  result: ResultView;
  message: string;
  messageId: number;
  missionToast: MissionToast | null;
  joystick: JoystickView;
}

const emptyMission: MissionView = {
  type: "collect",
  icon: "◆",
  label: "—",
  progress: 0,
  target: 1,
  done: false,
};

const initialState: GameUiState = {
  phase: "home",
  hud: {
    speed: 132,
    score: 0,
    runTokens: 0,
    multiplier: 1,
    combo: 0,
    flowProgress: 0,
    nearMisses: 0,
    modeLabel: "INFINI",
    timeRemaining: null,
    finalCountdown: null,
    stage: 1,
    levelProgress: 0,
    boost: 0,
    nitroEnergy: 0,
    nitroMaxEnergy: 2,
    unlimitedNitro: false,
    magnet: 0,
    doubleScore: 0,
    reverse: 0,
    speedLimit: 0,
    steeringLimit: 0,
    chaos: 0,
    boostBlend: 0,
  },
  meta: {
    best: 0,
    tokens: 0,
    set: 1,
    multiplier: 1,
    completed: 0,
    missions: [],
    activeMission: emptyMission,
  },
  result: {
    score: 0,
    distance: 0,
    duration: 0,
    stage: 1,
    runTokens: 0,
    nearMisses: 0,
    bestCombo: 0,
    modeLabel: "INFINI",
    outcome: "crash",
  },
  message: "",
  messageId: 0,
  missionToast: null,
  joystick: { visible: false, x: 0, y: 0, dx: 0, dy: 0 },
};

export function createGameBus(seed: GameUiState = initialState) {
  let snapshot = seed;
  let missionToastSequence = seed.missionToast?.id ?? 0;
  const listeners = new Set<() => void>();
  const update = (patch: Partial<GameUiState>) => {
    snapshot = { ...snapshot, ...patch };
    listeners.forEach((listener) => listener());
  };

  return {
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setPhase: (phase: GamePhase) => update({ phase }),
    setHud: (hud: HudView) => update({ hud }),
    setMeta: (meta: MetaView) => update({ meta }),
    setResult: (result: ResultView) => update({ result }),
    showMessage(message: string) {
      update({ message, messageId: snapshot.messageId + 1 });
    },
    clearMessage() {
      if (snapshot.message) update({ message: "" });
    },
    showMissionToast(toast: Omit<MissionToast, "id">) {
      update({ missionToast: { ...toast, id: ++missionToastSequence } });
    },
    clearMissionToast() {
      if (snapshot.missionToast) update({ missionToast: null });
    },
    setJoystick: (joystick: JoystickView) => update({ joystick }),
  };
}

export type GameBus = ReturnType<typeof createGameBus>;
export const gameBus = createGameBus();
